import { useAuth } from "./components/AuthContext";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import NavBar from "./components/NavBar";
import { ShoppingCartProvider } from "./context/ShoppingCartContext";
import { CompletedTimersProvider } from "./context/CompletedTimersContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LandingPage from "./components/LandingPage";
import Login from "./components/Login";
import Register from "./components/Register";
import MemberProfile from "./components/MemberProfile";
import Contact from "./components/Contact";
import Bookings from "./components/Bookings";
import Payment from "./components/Payment";
import Checkout from "./components/Checkout";
import Features from "./components/Features";
import ProductsPage from "./components/ProductsPage";
import InfraredSaunaInfo from "./components/InfraredSaunaInfo";
import ChromotherapyInfo from "./components/ChromotherapyInfo";
import HalotherapyInfo from "./components/HalotherapyInfo";
import RedLightTherapyInfo from "./components/RedLightTherapyInfo";
import MemberDashboard from "./components/member-dashboard/MemberDashboard";
import LuminaryDashboard from "./components/LuminaryDashboard";
import MembershipsPage from "./components/MembershipsPage";
import AboutUs from "./components/AboutUs";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import ChangePasswordPrompt from "./components/ChangePasswordPrompt";
import CartPage from "./components/CartPage";
import BookSession from "./components/BookSession";
import StaffDashboard from "./components/StaffDashboard";
import ActiveSessions from "./components/staff/ActiveSessions";
import StaffEditBooking from "./components/StaffEditBooking";
import SuiteAssignmentPage from "./components/SuiteAssignmentPage";
import AnalyticsDashboard from "./components/staff/AnalyticsDashboard";
// FEEDBACK-SOFT-HIDE-001: FeedbackManagement import removed.
//   /staff/feedback admin route is unmounted. Backend endpoints remain so
//   Testimonials.js continues to read historical data.
import CustomerProfile from "./components/staff/CustomerProfile";
import CustomerList from "./components/staff/CustomerList";
import CorporateProgramsPage from "./components/staff/CorporateProgramsPage";
import ProviderAvailabilityPage from "./components/staff/ProviderAvailabilityPage";
import TherapistPortalPage from "./components/therapist/TherapistPortalPage";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import Success from "./components/Success";
import Cancel from "./components/Cancel";
import StudioPackagesPage from "./components/StudioPackagesPage";
import DropInsPage from "./components/DropInsPage";
import IntroOffersPage from "./components/IntroOffersPage";
import GiftCardsPage from "./components/GiftCardsPage";
import GiftSuccessPage from "./components/GiftSuccessPage";
import GiftRedemptionPage from "./components/GiftRedemptionPage";
// PROMO-RF (hotfix 2026-06-20): legacy in-house PromoCodeManagement component
//   removed from import list — the `/staff/promo-codes` route is now a
//   redirect to `/staff/promo-management` (PROMO-RF-E/F Stripe-authoritative
//   StaffPromoManagementPage). Root cause: legacy `/api/promo-codes` BE mount
//   was removed (backend/index.js:561-567); the legacy component still hit
//   that route and 404'd. The full RF-E/F replacement page is already
//   deployed at `/staff/promo-management` with create/archive/filter parity
//   or superset of the legacy form. Component file retained on disk for
//   forensic value pending RF-H final cleanup (ADR-029 D4).
import BlogDashboard from "./components/blog/admin/BlogDashboard";
import BlogPostForm from "./components/blog/admin/BlogPostForm";
import BlogPostReview from "./components/blog/admin/BlogPostReview";
import BlogList from "./components/blog/reader/BlogList";
import BlogPost from "./components/blog/reader/BlogPost";
import BlogPreview from "./components/blog/reader/BlogPreview";
import RegiftPage from "./components/RegiftPage";
import RejectGiftPage from "./components/RejectGiftPage";
import ScrollToTop from "./components/ScrollToTop";
import SuiteCalendarDashboard from "./components/SuiteCalendarDashboard";
import BloggerDashboard from "./components/blog/author/BloggerDashboard";
import StudioSettingsPage from "./components/staff/StudioSettingsPage";
import SalesReportPage from "./components/staff/SalesReportPage";
import StaffProfileList from "./components/staff/StaffProfileList";
import StaffProfileForm from "./components/staff/StaffProfileForm";
import JobCodeList from "./components/staff/JobCodeList";
import ClockInOutScreen from "./components/staff/ClockInOutScreen";
import DailyReviewDashboard from "./components/staff/DailyReviewDashboard";
import TimesheetReview from "./components/staff/TimesheetReview";
import ScheduleAuditPage from "./components/staff/ScheduleAuditPage";
import MyScheduleView from "./components/staff/MyScheduleView";
import ScheduleBuilder from "./components/staff/ScheduleBuilder";
import StaffAvailabilityPage from "./components/staff/StaffAvailabilityPage";
import AdminStaffAvailabilityDashboard from "./components/staff/AdminStaffAvailabilityDashboard";
import BroadcastComposePage from "./components/staff/BroadcastComposePage";
import BroadcastHistoryPage from "./components/staff/BroadcastHistoryPage";
import StaffPromoManagementPage from "./components/staff/StaffPromoManagementPage";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CommerceDisclosures from "./components/CommerceDisclosures";
import StudioPolicy from "./components/StudioPolicy";
// FEEDBACK-SOFT-HIDE-001: FeedbackPage import removed; /feedback now redirects to "/".
import Footer from "./components/Footer";

// ESIGN-WAIVER-CONSENT-001 — first-booking gate + return-from-SignWell
// receipt page (Zuri spec §16.2 + §16.4 / §16.5).
import WaiverGate from "./components/WaiverGate";
import WaiverReturn from "./components/WaiverReturn";

// Import POS components
import POSMain from "./components/pos/POSMain";

// Import the new SuiteCalendar component.
import SuiteCalendar from "./components/SuiteCalendar";

// A simple wrapper to extract route params and pass them to SuiteCalendar.
import { useParams } from "react-router-dom";
const SuiteCalendarWrapper = () => {
  const { suiteType, suiteNumber } = useParams();
  return (
    <SuiteCalendar suiteType={suiteType} suiteNumber={Number(suiteNumber)} />
  );
};

function App() {
  const { token, initialized, login, logout } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      // Log the token for debugging
      //console.log("App.js - Token available:", token);

      // Try to decode the token to check the role
      try {
        const decoded = jwtDecode(token);
        //console.log("App.js - Decoded token:", decoded);
      } catch (error) {
        console.error("App.js - Error decoding token:", error);
      }

      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          //console.log("App.js - Profile data:", response.data);
          setUserProfile(response.data);
        })
        .catch((error) => {
          console.error("Error fetching user profile:", error);
          setUserProfile(null);
        });
    } else {
      setUserProfile(null);
    }
  }, [token]);

  const handleLogin = async (sessionData) => {
    try {
      const token = sessionData.token;
      // Decode the token to get user information
      const decodedToken = jwtDecode(token);
      await login(sessionData);

      // Redirect based on user role
      if (decodedToken.role === "staff" || decodedToken.role === "admin") {
        navigate("/staff");
      } else if (decodedToken.role === "massage_therapist") {
        navigate("/therapist");
      } else if (decodedToken.role === "luminary") {
        navigate("/luminary");
      } else if (decodedToken.role === "blogger") {
        navigate("/blogger");
      } else if (location.state?.from === "/cart") {
        navigate("/cart");
      } else {
        navigate("/member");
      }
    } catch (error) {
      console.error("Error in handleLogin:", error);
      if (sessionData?.token) {
        await login(sessionData);
      }
      navigate("/member");
    }
  };

  const handleLogout = async () => {
    await logout();
    setUserProfile(null);
  };

  return (
    <ShoppingCartProvider token={token}>
      <CompletedTimersProvider>
        <div className="app-container">
          <NavBar onLogout={handleLogout} />
          <ScrollToTop />
          <ToastContainer />
          <Routes>
            <Route path="/" element={<LandingPage token={token} />} />
            <Route
              path="/staff/active-sessions"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <ActiveSessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute token={token} initialized={initialized}>
                  <Login onLogin={handleLogin} />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute token={token} initialized={initialized}>
                  <Register />
                </PublicRoute>
              }
            />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/features" element={<Features />} />
            <Route path="/profile" element={<MemberProfile />} />
            <Route path="/infrared-info" element={<InfraredSaunaInfo />} />
            <Route path="/chromotherapy-info" element={<ChromotherapyInfo />} />
            <Route path="/halotherapy-info" element={<HalotherapyInfo />} />
            <Route path="/redlight-info" element={<RedLightTherapyInfo />} />
            {/* Public Blog Preview Route */}
            <Route path="/blog-preview" element={<BlogPreview />} />
            <Route
              path="/staff/edit-booking/:id"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <StaffEditBooking />
                </ProtectedRoute>
              }
            />
            {/* PROMO-RF (hotfix 2026-06-20): legacy `/staff/promo-codes`
                route now redirects to the deployed Stripe-authoritative
                `/staff/promo-management` (PROMO-RF-E/F StaffPromoManagementPage).
                The legacy in-house BE route `/api/promo-codes` was unmounted
                (backend/index.js:561-567); the legacy component still called
                it and 404'd in prod. RF-E/F page provides equal-or-superset
                of the legacy functionality (list + filter + create + archive).
                Existing sidebar links / bookmarks to `/staff/promo-codes`
                continue to work via this redirect. Single-line revert path.
                ADR-029 D4 — final RF-H cleanup will retire this route and
                the orphaned PromoCodeManagement/PromoCodeList/PromoCodeForm
                component files. */}
            <Route
              path="/staff/promo-codes"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <Navigate to="/staff/promo-management" replace />
                </ProtectedRoute>
              }
            />
            <Route path="/products" element={<ProductsPage token={token} />} />
            <Route path="/success" element={<Success />} />
            <Route path="/cancel" element={<Cancel />} />
            <Route path="/memberships" element={<MembershipsPage />} />
            {/* Studio Packages now available */}
            <Route path="/studio-packages" element={<StudioPackagesPage />} />
            {/* Single Sessions now available */}
            <Route path="/drop-in-sessions" element={<DropInsPage />} />
            {/* Introductory Offers disabled during trial period */}
            {/* <Route path="/intro-offers" element={<IntroOffersPage />} /> */}
            <Route path="/gift-cards" element={<GiftCardsPage />} />
            <Route path="/gift-success" element={<GiftSuccessPage />} />
            <Route path="/redeem/:code" element={<GiftRedemptionPage />} />
            <Route path="/reject-gift/:code" element={<RejectGiftPage />} />
            <Route
              path="/regift/:giftId"
              element={
                <ProtectedRoute token={token} allowedRoles={["member"]}>
                  <RegiftPage />
                </ProtectedRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={[
                    "member",
                    "staff",
                    "admin",
                    "massage_therapist",
                    "luminary",
                  ]}
                >
                  <ChangePasswordPrompt />
                </ProtectedRoute>
              }
            />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/commerce-disclosures"
              element={<CommerceDisclosures />}
            />
            <Route path="/studio-policy" element={<StudioPolicy />} />
            {/* FEEDBACK-SOFT-HIDE-001: Customer-facing /feedback unmounted.
                Direct visits redirect to home. Backend Feedback model + endpoints
                intact for Testimonials.js historical read path. */}
            <Route path="/feedback" element={<Navigate to="/" replace />} />

            {/* Protected routes */}
            <Route
              path="/member"
              element={
                <ProtectedRoute token={token} allowedRoles={["member"]}>
                  <MemberDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/luminary"
              element={
                <ProtectedRoute token={token} allowedRoles={["luminary"]}>
                  <LuminaryDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/therapist"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["massage_therapist", "staff", "admin"]}
                >
                  <TherapistPortalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/book-session"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["member", "staff", "admin"]}
                >
                  <BookSession />
                </ProtectedRoute>
              }
            />
            {/* ESIGN-WAIVER-CONSENT-001 — first-booking gate + return-from-SignWell receipt. */}
            <Route
              path="/waivers/sign-required"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["member", "staff", "admin"]}
                >
                  <WaiverGate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/waivers/return"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["member", "staff", "admin"]}
                >
                  <WaiverReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suite-assignments"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <SuiteAssignmentPage />
                </ProtectedRoute>
              }
            />
            {/* New route for suite calendar */}
            <Route
              path="/staff/suite-calendar/:suiteType/:suiteNumber"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <SuiteCalendarWrapper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/all-suite-calendars"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <SuiteCalendarDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/analytics"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            {/* FEEDBACK-SOFT-HIDE-001: /staff/feedback admin route unmounted.
                FeedbackManagement component file deleted from the FE tree;
                backend endpoints intact for Testimonials.js. */}
            <Route
              path="/staff/customers"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <CustomerList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/customers/:customerId"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <CustomerProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/corporate-programs"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <CorporateProgramsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/massage-availability"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <ProviderAvailabilityPage />
                </ProtectedRoute>
              }
            />
            {/* POS Route for Stripe Terminal */}
            <Route
              path="/pos"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <POSMain />
                </ProtectedRoute>
              }
            />
            {/* Studio Settings Route */}
            <Route
              path="/staff/studio-settings"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <StudioSettingsPage />
                </ProtectedRoute>
              }
            />
            {/* Sales report (Exhibit G). Admins and holders of the
                "sales_report" permission; the server checks the permission. */}
            <Route
              path="/staff/sales-report"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <SalesReportPage />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Staff Profile management (TIME-001) — admin only */}
            <Route
              path="/staff/staff-profiles"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <StaffProfileList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/staff-profiles/new"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <StaffProfileForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/staff-profiles/:id"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <StaffProfileForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/job-codes"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <JobCodeList />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Clock In / Clock Out (TIME-004a) — any authenticated
                staff role can self-clock; mobile-web responsive. */}
            <Route
              path="/staff/clock"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={[
                    "staff",
                    "admin",
                    "massage_therapist",
                    "luminary",
                  ]}
                >
                  <ClockInOutScreen />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Daily Review Dashboard (TIME-009) — admin-only live
                view of currently-clocked-in staff. */}
            <Route
              path="/staff/timelogs/today"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <DailyReviewDashboard />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Timesheet Review (PAY-005) — admin-only per-period
                rollup + per-row Approve/Reject + period lifecycle transitions. */}
            <Route
              path="/staff/payroll/timesheets"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <TimesheetReview />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Schedule Builder (SCHED-SLICE1-E) — admin-only
                weekly drag-and-drop calendar build + publish + conflict UX.
                Pure-additive route; single-revert posture per Kenya intent
                notice 2026-05-11 22:30. */}
            <Route
              path="/staff/schedule"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <ScheduleBuilder />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Schedule Audit (SCHED-SLICE1-F) — admin-only per-week
                audit trail for the workforce scheduling Slice 1 feature.
                Per-shift drawer is mounted inside the Edit Shift modal (Story E);
                this route is the full-screen per-week page. */}
            <Route
              path="/staff/schedule/audit"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <ScheduleAuditPage />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Staff Schedule View (SCHED-SLICE1-G) — read-only
                weekly calendar of own published shifts. Staff-facing surface
                with NO admin chrome. RBAC: any role with a backing
                StaffProfile (staff, admin, massage_therapist). BE enforces
                own-shifts-only redaction; FE is defense-in-depth. */}
            <Route
              path="/staff/my-schedule"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["staff", "admin", "massage_therapist"]}
                >
                  <MyScheduleView />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Staff self-service availability (SCHED-SLICE1-H).
                Consumes SCHED-SLICE1-D BE endpoints. Role-gated to the same
                roster as the BE controller's `requireStaff` (admin counts
                as staff). Pure-additive; single-revert posture. */}
            <Route
              path="/staff/my-availability"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["staff", "admin", "massage_therapist"]}
                >
                  <StaffAvailabilityPage />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Admin "All Availability" Dashboard
                (SCHED-AVAIL-DASHBOARD-001-B). Admin-only matrix page that
                aggregates every active staff member's recurring weekly
                availability into a Sun-Sat x staff grid. Drills into the
                existing SCHED-1K AdminSetAvailabilityModal verbatim — no new
                modal authored. Pure-additive route; single-revert posture. */}
            <Route
              path="/staff/availability"
              element={
                <ProtectedRoute token={token} allowedRoles={["admin"]}>
                  <AdminStaffAvailabilityDashboard />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Send Communication compose page
                (MAILCHIMP-COMMS-001-B). RBAC admin + staff per Roderick
                Sprint 9 lock; BE re-checks via authorizeBroadcastSender on
                /api/comms/broadcast. Pure-additive route. Tech doc:
                docs/architecture/mailchimp-comms-001-A-tech.md. Visual:
                docs/design/MAILCHIMP-COMMS-001-A-design.md. */}
            <Route
              path="/staff/communications/broadcast"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["admin", "staff", "massage_therapist"]}
                >
                  <BroadcastComposePage />
                </ProtectedRoute>
              }
            />
            {/* Workforce — Broadcast Send History (MAILCHIMP-COMMS-001-C).
                Read-only paginated table reading GET /api/comms/broadcast,
                with a row-click detail drawer reading
                GET /api/comms/broadcast/:id. Same RBAC as compose; BE
                staff-scopes the list query to senderUserId=self regardless
                of input. Pure-additive route. Tech doc:
                docs/architecture/mailchimp-comms-001-A-tech.md §"GET
                /api/comms/broadcast". Visual:
                docs/design/MAILCHIMP-COMMS-001-A-design.md §5-§6. */}
            <Route
              path="/staff/communications/history"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["admin", "staff", "massage_therapist"]}
                >
                  <BroadcastHistoryPage />
                </ProtectedRoute>
              }
            />
            {/* PROMO-RF-E — Staff Promo Code Management (list + detail).
                Stripe-sourced via the PROMO-RF-B pass-through endpoints
                (GET /api/staff/promo-codes [+ /:id]). DISTINCT from the
                LEGACY in-house /staff/promo-codes route above (left intact
                for PROMO-RF-H per ADR-029 D4). Staff-gated (AC Scenario 5);
                BE re-checks via authorizeRoles("staff","admin"). RF-F adds
                create+archive onto the same component surface. Pure-additive
                route. ADR: docs/architecture/ADR-029-promo-reframe-reconcile.md
                (D2/D5). Story:
                docs/backlog/PROMO-RF-E-staff-promo-management-list-view.md */}
            <Route
              path="/staff/promo-management"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <StaffPromoManagementPage />
                </ProtectedRoute>
              }
            />
            {/* Admin/Staff Blog Routes */}
            <Route
              path="/staff/blog"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <BlogDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/blog/create"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["staff", "admin", "blogger"]}
                >
                  <BlogPostForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blogger"
              element={
                <ProtectedRoute token={token} allowedRoles={["blogger"]}>
                  <BloggerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/blog/edit/:postSlug"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["staff", "admin", "blogger"]}
                >
                  <BlogPostForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/blog/review/:postId"
              element={
                <ProtectedRoute token={token} allowedRoles={["staff", "admin"]}>
                  <BlogPostReview />
                </ProtectedRoute>
              }
            />

            {/* Member Blog Routes */}
            <Route
              path="/blog"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["member", "staff", "admin", "blogger"]}
                >
                  <BlogList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blog/:slug"
              element={
                <ProtectedRoute
                  token={token}
                  allowedRoles={["member", "staff", "admin", "blogger"]}
                >
                  <BlogPost />
                </ProtectedRoute>
              }
            />
            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <Footer />
        </div>
      </CompletedTimersProvider>
    </ShoppingCartProvider>
  );
}

export default App;
